/**
 * Admin-authored announcement bars for pre-login surfaces — rendered by
 * `PublicShell`, so every unauthenticated page (sign-in, get-started, public
 * menu) carries them. This is the surface for "the app changed, here's how to
 * sign in" notices that must reach users who can't get in yet.
 *
 * Content comes from `GET /api/public/banners` (global PRE_LOGIN/ALL banners
 * inside their window). There is no viewer, so dismissal is per-browser via
 * localStorage rather than `BannerDismissal` rows. Renders nothing while
 * loading — a skeleton bar would shift the sign-in card on every cold load.
 */

import { useState } from 'react'

import { usePreLoginBanners } from '@/api/banners'
import {
  readDismissedBannerIds,
  rememberDismissedBannerId,
  undismissedBanners,
} from '@/lib/pre-login-banner-dismissals'

import { BannerBar } from './announcement-banners'

export function PreLoginBanners() {
  const { data } = usePreLoginBanners()
  // Seeded once from storage; the setter re-renders on dismiss.
  const [dismissedIds, setDismissedIds] = useState<string[]>(readDismissedBannerIds)

  const banners = undismissedBanners(data?.banners ?? [], dismissedIds)
  if (banners.length === 0) {
    return null
  }

  return (
    <div data-testid="pre-login-banners">
      {banners.map((banner) => (
        <BannerBar
          key={banner.id}
          banner={banner}
          onDismiss={() => setDismissedIds(rememberDismissedBannerId(banner.id))}
        />
      ))}
    </div>
  )
}
